import { compact } from 'lodash-es';

import { SearchFilterState } from '~/types';
import { FilterValue, SearchFilter } from '~/apis/search';
import { findBreadcrumb } from '~/services/category';

export const getUsedText = (used: boolean | null) => {
  switch (used) {
    case false:
      return '새상품';
    case true:
      return '중고상품';
    default:
      return '';
  }
};

export const getPriceRangeText = (minPrice: string, maxPrice: string) => {
  if (!minPrice && !maxPrice) return '';

  if (minPrice && !maxPrice) return `${Number(minPrice).toLocaleString()}원 이상`;
  if (!minPrice && maxPrice) return `${Number(maxPrice).toLocaleString()}원 이하`;
  if (minPrice && maxPrice) return `${Number(minPrice).toLocaleString()}원 - ${Number(maxPrice).toLocaleString()}원`;

  return '';
};

export const getWithinText = (text: string) => {
  return text.length > 10 ? `‘${text.slice(0, 10)}’...` : `‘${text.slice(0, 10)}’ ` + '재검색';
};

export const getMultiSelectionText = (filters: FilterValue[], ids: string[]) => {
  const selectionNames = compact(
    ids.map((id) => {
      return filters.find((filter) => filter.value === id)?.name;
    }),
  );

  if (selectionNames.length === 0) return '';
  if (selectionNames.length === 1) return selectionNames[0];

  return `${selectionNames[0]} 외 ${selectionNames.length - 1}`;
};

const getCategoryText = (categories: FilterValue[], id: string) => {
  const categoryBreadCrumb = findBreadcrumb(id, categories);

  // 카테고리 BreadCrumb이 다 채워졌으면, 가장 마지막 name이 최종 선택한 항목
  if (categoryBreadCrumb[categoryBreadCrumb.length - 1]?.name)
    return categoryBreadCrumb[categoryBreadCrumb.length - 1]?.name;

  // 카테고리 BreadCrumb이 아직 다 안채워졌으면, 마지막에서 2번째 name이 최종 선택한 항목
  if (categoryBreadCrumb[categoryBreadCrumb.length - 2]?.name)
    return categoryBreadCrumb[categoryBreadCrumb.length - 2]?.name;
};

const getRemainderFilterBubbleFormattedText = (values: FilterValue[], value: string) => {
  return values.find((v) => v.value === value)?.name ?? '';
};

export const getFilterBubbleFormattedText = (key: string, value: unknown[], filters: SearchFilter[]) => {
  switch (key) {
    case 'price':
      return getPriceRangeText(String(value[0]), String(value[1]));
    case 'used':
      return getUsedText(typeof value[0] === 'boolean' ? Boolean(value[0]) : null);
    case 'within':
      return getWithinText(String(value[0]));
    case 'brandId':
    case 'modelId':
      return getMultiSelectionText(filters.find((filter) => filter.key === key)?.values ?? [], value.map(String));
    case 'categoryId':
      return getCategoryText(filters?.find((filter) => filter.key === 'categoryId')?.values ?? [], String(value));
    case 'soldout':
      return (
        '판매완료 ' +
        getRemainderFilterBubbleFormattedText(
          filters?.find((filter) => filter.key === key)?.values ?? [],
          String(value[0]),
        )
      );

    default:
      return getRemainderFilterBubbleFormattedText(
        filters?.find((filter) => filter.key === key)?.values ?? [],
        String(value[0]),
      );
  }
};

export const getSelectedKeys = (filterData: SearchFilterState, filters: SearchFilter[] = []) => {
  const selectedFilter: string[] = [];

  let minPriceExist = false;
  let maxPriceExist = false;

  for (const [key, value] of Object.entries(filterData)) {
    if (key === 'minPrice' || key === 'maxPrice') {
      if (value) {
        key === 'minPrice' ? (minPriceExist = true) : (maxPriceExist = true);
      }

      continue;
    }

    // value가 true,false인 boolean 값인 경우도 있어서 undefined로 처리
    if (value !== undefined) selectedFilter.push(key);
  }

  if (minPriceExist || maxPriceExist) selectedFilter.push('price');

  return {
    // shortcut이 true인 경우에만, 유효한 selectedFilter
    shortcutKeys: selectedFilter.filter((v) => {
      return filters.find(({ id, key }) => id === v || key === v)?.shortcut;
    }),
    keys: selectedFilter,
  };
};

export const getIsHighlighted = (selectedKeys: string[] = [], filters: SearchFilter[] = []) => {
  return filters.some(({ highlight, id, key }) => {
    if (!highlight) return false;
    if (selectedKeys.includes(key ?? '') || selectedKeys.includes(id)) return true;
    // 가격은 queryString에서 max/minPrice로 표현을 해서(api body 때문에),
    // 예외 처리가 필요
    if (
      selectedKeys.includes('price') &&
      id === 'price' &&
      (selectedKeys.includes('minPrice') || selectedKeys.includes('maxPrice'))
    )
      return true;
  });
};
